/**
 * Adjust clip — playhead-first trim panel + speaker quick-fix, rendered as
 * ONE card directly under the preview player with ONE Apply. The operator
 * scrubs the video, pauses where the good part starts/ends, and presses
 * "Start here" / "End here"; fine-nudge steppers (±1s/±5s per edge) adjust
 * from there. A debounced dry-run preview (GET /trim-preview) shows the new
 * length, the exact words a CUT edge would start/end on, and any warnings,
 * before committing.
 *
 * The speaker row (chips for the people this clip knows + a free input)
 * lives in the same card so a wrong-name fix and a re-cut are ONE action:
 *   - speaker only  → card-only respeaker probe (~1 min, POST /edit/speaker)
 *   - bounds only   → full re-run (~5–10 min, POST /edit/trim)
 *   - both          → ONE full re-run; the speaker override rides the trim
 *                     (POST /edit/trim with speakerName) — never two renders.
 * Upload jobs (trimmable=false) have no re-cuttable source: the trim strip
 * hides entirely and the card degrades to the speaker row alone.
 *
 * Delta semantics (clip-CONTENT seconds — the mp4 timeline minus the baked
 * cover hold, which this panel learns from the baseline preview on mount):
 *   startDelta > 0 cuts the head; < 0 starts earlier (extend).
 *   endDelta   > 0 extends the tail; < 0 cuts it.
 *
 * Two-stage disclosure: the zero state is one slim strip (clip bar + the two
 * hero buttons) plus the speaker row. Steppers, preview line, and the CTA
 * row materialize once something changed.
 *
 * Boundary-word quoting rule: words are quoted ONLY for edges that moved
 * inward (cuts) — an extended edge's content isn't in the current mp4, so
 * the copy describes the sentence-snap behavior instead.
 */
import { AlertTriangle, Crosshair, Loader2, Play, Scissors, UserRound } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, MouseEvent as ReactMouseEvent, RefObject } from "react";
import { applyTrim, editSpeakerName, HttpError, trimPreview, type JobInput, type TrimPreview } from "../api";
import { SmartExtendTrigger } from "./SmartExtendSection";
import { useIsMobile } from "../lib/useIsMobile";
import { ctaSmallStyle } from "./styles";

/** Deltas smaller than this are treated as "unchanged". */
const EPS = 0.05;
/** Server rejects |delta| > 90 — clamp client-side so steppers can't overrun. */
const DELTA_LIMIT = 90;
/** Preview debounce. */
const DEBOUNCE_MS = 400;
/** U5: playhead buttons stay disabled this far into the cover hold. */
const PLAYHEAD_MIN_PAST_COVER = 0.3;

const PLAYHEAD_TOOLTIP = "Play the clip and pause where the good part starts.";

/**
 * How the panel talks to WHATEVER is previewing the clip. The video page
 * hands a `<video>` ref; the Studio's stage is a Remotion Player, not a
 * `<video>`, so it hands this adapter instead — and the adapter wins when
 * both are present. Seconds are mp4-timeline seconds (cover hold included),
 * exactly what `<video>.currentTime` would be.
 */
export interface PreviewSeek {
  currentSec: () => number;
  seekTo: (sec: number) => void;
  /** Start playback (the audition buttons). Optional: a host without it just seeks. */
  play?: () => void;
  /** Total length in seconds, when known — the last-resort clip-length fallback. */
  durationSec?: () => number | undefined;
}

interface TrimExtendPanelProps {
  /** Structural subset of JobDetail's JobView — just what the panel needs. */
  view: { id: string; durationSec?: number };
  /** Ref to the shared preview <video>, owned by the video page's preview.
   *  Optional: a host with no `<video>` passes `seek` instead. */
  videoRef?: RefObject<HTMLVideoElement | null>;
  /** Player adapter — wins over `videoRef` when present (the Studio). */
  seek?: PreviewSeek;
  /** A re-cut / speaker job was started. The host follows it; absent ⇒ the
   *  page reloads (a finished job has no live stream to subscribe to). */
  onJobStarted?: () => void;
  /** Name currently rendered on the card (JobView.speaker). */
  currentSpeaker?: string;
  /** Submitted inputs — source of the speaker host/guest chips. */
  input?: JobInput;
  /** False for upload jobs (no input.from → no re-cuttable source): trim
   *  controls hide entirely, the speaker row still works. */
  trimmable: boolean;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;
const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/** 12.34 → "0:12.3"; rounds to 0.1s FIRST so 59.96 renders "1:00.0". */
function fmtClock(sec: number): string {
  const t = round1(Math.max(0, sec));
  const m = Math.floor(t / 60);
  const rem = round1(t - m * 60);
  return `${m}:${rem < 10 ? "0" : ""}${rem.toFixed(1)}`;
}

/** U6 vocabulary — START edge. */
function startChip(sd: number): string {
  if (sd >= EPS) return `cut ${sd.toFixed(1)}s`;
  if (sd <= -EPS) return `${(-sd).toFixed(1)}s earlier`;
  return "unchanged";
}

/** U6 vocabulary — END edge. */
function endChip(ed: number): string {
  if (ed <= -EPS) return `cut ${(-ed).toFixed(1)}s`;
  if (ed >= EPS) return `${ed.toFixed(1)}s longer`;
  return "unchanged";
}

/**
 * U4: compose the preview line. Cut edges quote the boundary words; edges
 * that moved OUTWARD (extends) never quote — their content isn't verifiable
 * from the current mp4, so the copy describes the sentence-snap instead.
 */
function previewLine(p: TrimPreview, sd: number, ed: number): string {
  const parts: string[] = [`≈${Math.round(p.newDurationEstSec)}s`];
  if (sd > 0.25) {
    if (p.newFirstWords) parts.push(`starts on “${p.newFirstWords}”`);
  } else if (sd < -0.25) {
    // Short stub only — the server warning below carries the sentence-snap
    // explanation; composing it twice showed the same copy back to back.
    parts.push(`starts ~${(-sd).toFixed(1)}s earlier`);
  }
  if (ed < -0.25) {
    if (p.newLastWords) parts.push(`ends after “${p.newLastWords}”`);
  } else if (ed > 0.25) {
    parts.push(`up to ~${ed.toFixed(1)}s longer`);
  }
  return parts.join(" · ");
}

/** Direction-coded stepper chip (matches the caption-sync convention:
 *  blue = earlier on the timeline, yellow = later). */
function stepBtnStyle(dir: "earlier" | "later", disabled: boolean): CSSProperties {
  const earlier = dir === "earlier";
  return {
    padding: "6px 9px",
    borderRadius: 8,
    fontSize: 11,
    fontFamily: "var(--ql-font-mono)",
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    background: earlier ? "rgba(96,165,250,.10)" : "rgba(255,213,0,.10)",
    border: earlier ? "1px solid rgba(96,165,250,.30)" : "1px solid rgba(255,213,0,.30)",
    color: earlier ? "#60A5FA" : "var(--ql-yellow)",
    opacity: disabled ? 0.5 : 1,
  };
}

function heroBtnStyle(enabled: boolean): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 12px",
    borderRadius: 10,
    background: "transparent",
    border: "1px solid rgba(255,213,0,.45)",
    color: "var(--ql-yellow)",
    fontSize: 12,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.45,
    whiteSpace: "nowrap",
  };
}

const auditionBtnStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 10px",
  borderRadius: 8,
  background: "rgba(255,255,255,.03)",
  border: "1px solid rgba(255,255,255,.10)",
  color: "var(--ql-fg-muted)",
  fontSize: 11,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};

const microLabelStyle: CSSProperties = {
  fontSize: 9,
  color: "var(--ql-fg-subtle)",
  letterSpacing: ".04em",
  textTransform: "uppercase",
};

export function TrimExtendPanel({ view, videoRef, seek, currentSpeaker, input, trimmable, onJobStarted }: TrimExtendPanelProps): React.JSX.Element {
  const isMobile = useIsMobile();

  // The ONE place the panel touches the preview. Every playhead read, seek
  // and audition below goes through it, so the page (`<video>`) and the
  // Studio (Player adapter) can't drift apart.
  const player = useMemo(() => ({
    currentSec: (): number | null =>
      seek ? seek.currentSec() : (videoRef?.current ? videoRef.current.currentTime : null),
    seekTo: (sec: number): void => {
      if (seek) seek.seekTo(sec);
      else if (videoRef?.current) videoRef.current.currentTime = sec;
    },
    play: (): void => {
      if (seek) seek.play?.();
      else if (videoRef?.current) void videoRef.current.play();
    },
    durationSec: (): number | undefined =>
      seek ? seek.durationSec?.() : videoRef?.current?.duration,
  }), [seek, videoRef]);

  const [startDelta, setStartDelta] = useState(0);
  const [endDelta, setEndDelta] = useState(0);
  // U2: once true (a playhead capture or a nudge happened), the detail stage
  // stays open even if the deltas are stepped back to exactly zero.
  const [engaged, setEngaged] = useState(false);

  // Speaker quick-fix state — pre-filled with the name currently on the card.
  const [speakerName, setSpeakerName] = useState<string>(currentSpeaker ?? "");

  // Baseline plan (deltas 0/0) fetched on mount — teaches us coverHoldSec and
  // the current content length so playhead time converts to clip time.
  const [baseline, setBaseline] = useState<TrimPreview | null>(null);
  const [baselineError, setBaselineError] = useState<string | null>(null);
  const [coverHold, setCoverHold] = useState<number | null>(null);

  const [preview, setPreview] = useState<TrimPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewPending, setPreviewPending] = useState(false);

  const [busy, setBusy] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  // Low-freq mirror of the shared player's currentTime — drives the playhead
  // marker, the live hero-button time, and the U5 disabled gate. When the
  // video is paused the value doesn't change, so React bails on re-renders.
  const [playheadSec, setPlayheadSec] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => {
      const t = player.currentSec();
      if (t !== null) setPlayheadSec(t);
    }, 250);
    return () => window.clearInterval(id);
  }, [player]);

  // Baseline fetch on mount. 0/0 is a valid GET (special-cased server-side).
  // Skipped for non-trimmable jobs — there is no plan to fetch.
  useEffect(() => {
    if (!trimmable) return;
    let cancelled = false;
    trimPreview(view.id, 0, 0)
      .then((p) => {
        if (cancelled) return;
        setBaseline(p);
        setCoverHold(p.coverHoldSec);
        setBaselineError(null);
      })
      .catch((err) => {
        if (!cancelled) setBaselineError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [view.id, trimmable]);

  // Debounced dry-run preview whenever a real delta exists. A sequence
  // counter drops stale responses (rapid stepper clicks).
  const reqSeq = useRef(0);
  useEffect(() => {
    setPostError(null);
    const sd = round1(startDelta);
    const ed = round1(endDelta);
    if (Math.abs(sd) < EPS && Math.abs(ed) < EPS) {
      reqSeq.current++; // drop any in-flight response — a stale plan must not repopulate after Reset
      setPreview(null);
      setPreviewError(null);
      setPreviewPending(false);
      return;
    }
    setPreviewPending(true);
    const seq = ++reqSeq.current;
    const t = window.setTimeout(() => {
      trimPreview(view.id, sd, ed)
        .then((p) => {
          if (reqSeq.current !== seq) return;
          setPreview(p);
          setCoverHold(p.coverHoldSec);
          setPreviewError(null);
          setPreviewPending(false);
        })
        .catch((err) => {
          if (reqSeq.current !== seq) return;
          setPreview(null);
          setPreviewError(err instanceof Error ? err.message : String(err));
          setPreviewPending(false);
        });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [view.id, startDelta, endDelta]);

  const coverHoldSec = coverHold ?? 0;
  // Baseline content length. Prefer the server's ffprobe-grounded figure;
  // fall back to the job's advisory durationSec, then the player's metadata.
  const videoDur = player.durationSec();
  const contentLen =
    baseline?.newDurationEstSec ??
    view.durationSec ??
    (typeof videoDur === "number" && Number.isFinite(videoDur)
      ? Math.max(0, videoDur - coverHoldSec)
      : null);

  const hasDelta = Math.abs(startDelta) >= EPS || Math.abs(endDelta) >= EPS;
  const showDetail = engaged || hasDelta;

  // The people this clip knows about: host (often a slug — prettify for
  // display AND submission) plus every guest. Deduped case-insensitively.
  const candidates: string[] = [];
  const pushCandidate = (n?: string): void => {
    const v = (n ?? "").trim();
    if (!v) return;
    if (candidates.some((c) => c.toLowerCase() === v.toLowerCase())) return;
    candidates.push(v);
  };
  pushCandidate(prettifySlug(input?.host));
  for (const g of input?.guests ?? []) pushCandidate(g);

  const speakerChanged =
    speakerName.trim() !== "" && speakerName.trim() !== (currentSpeaker ?? "").trim();
  // What one press of Apply will do — the whole point of the unified panel:
  // a speaker fix and a re-cut are never two waits. "both" sends the speaker
  // along with the trim so ONE full re-run covers everything.
  const mode: "none" | "speaker" | "bounds" | "both" =
    hasDelta && speakerChanged ? "both" : hasDelta ? "bounds" : speakerChanged ? "speaker" : "none";
  const playheadClipSec = Math.max(0, playheadSec - coverHoldSec);
  // U5 gate — also requires the baseline so coverHoldSec is known (a capture
  // before that would silently include the baked cover in the delta).
  const playheadUsable = coverHold !== null && playheadSec > coverHoldSec + PLAYHEAD_MIN_PAST_COVER;
  const canEndHere = playheadUsable && contentLen !== null;

  const seekToClipTime = (clipT: number): void => {
    player.seekTo(coverHoldSec + Math.max(0, clipT));
  };

  const captureStart = (): void => {
    if (!playheadUsable || busy) return;
    setStartDelta(round1(clamp(playheadClipSec, 0, DELTA_LIMIT)));
    setEngaged(true);
  };

  const captureEnd = (): void => {
    if (!canEndHere || busy || contentLen === null) return;
    // Pausing inside the mp4 can only pull the end EARLIER (a cut) — the
    // playhead can't reach content that doesn't exist yet.
    setEndDelta(round1(clamp(playheadClipSec - contentLen, -DELTA_LIMIT, 0)));
    setEngaged(true);
  };

  const nudgeStart = (d: number): void => {
    if (busy) return;
    setStartDelta((cur) => round1(clamp(cur + d, -DELTA_LIMIT, DELTA_LIMIT)));
    setEngaged(true);
  };

  const nudgeEnd = (d: number): void => {
    if (busy) return;
    setEndDelta((cur) => round1(clamp(cur + d, -DELTA_LIMIT, DELTA_LIMIT)));
    setEngaged(true);
  };

  const reset = (): void => {
    if (busy) return;
    setStartDelta(0);
    setEndDelta(0);
    setEngaged(false);
    setPreview(null);
    setPreviewError(null);
    setPostError(null);
    setSpeakerName(currentSpeaker ?? "");
  };

  // U8 audition — cuts only: the content at the new boundary is inside the
  // current mp4, so seeking the shared player previews it offline.
  const auditionStart = (): void => {
    player.seekTo(coverHoldSec + startDelta);
    player.play();
  };
  const auditionEnd = (): void => {
    if (contentLen === null) return;
    const newEnd = contentLen + endDelta;
    player.seekTo(coverHoldSec + Math.max(0, newEnd - 3));
    player.play();
  };

  const apply = async (): Promise<void> => {
    if (busy || mode === "none") return;
    setBusy(true);
    setPostError(null);
    try {
      if (mode === "speaker") {
        // Speaker only — keep the cheap card-only respeaker probe (~1 min).
        await editSpeakerName(view.id, speakerName.trim());
      } else {
        // Bounds (and, for "both", the speaker riding the SAME re-cut).
        await applyTrim(
          view.id,
          round1(startDelta),
          round1(endDelta),
          mode === "both" ? speakerName.trim() : undefined,
        );
      }
      // A host that follows jobs (the Studio) takes over here. Otherwise: a
      // finished job has no live SSE subscription, so the view would stay
      // frozen on 'ready' with a deleted mp4 behind it. Reload — the job
      // comes back queued/rendering, JobDetail opens its SSE stream, and the
      // operator watches the re-cut progress (same pattern as the speaker
      // edit and Restore flows). `busy` stays true until the reload lands.
      if (onJobStarted) onJobStarted();
      else window.location.reload();
    } catch (err) {
      if (err instanceof HttpError && err.status === 409) {
        setPostError("This clip is already re-rendering — wait for it to finish.");
      } else {
        setPostError(err instanceof Error ? err.message : String(err));
      }
      setBusy(false);
    }
  };

  // Speaker-only never depends on a trim plan, so a preview error must not
  // block it; anything involving bounds does.
  const ctaDisabled = busy || mode === "none" || (mode !== "speaker" && previewError !== null);
  const ctaLabel =
    mode === "speaker" ? "Update card"
    : mode === "both" ? "Re-cut + update speaker"
    : "Re-cut with these bounds";
  const ctaCost =
    mode === "speaker" ? "card-only · ~1 min"
    : mode === "both" ? "one full re-run · ~5–10 min"
    : "full re-run · ~5–10 min";
  const ctaTitle =
    mode === "speaker" ? "Re-render the name card with this name (~1 min)"
    : mode === "both" ? "One full re-run at the new bounds, with the corrected speaker on the card (~5–10 min)"
    : mode === "bounds" ? "Re-run the pipeline at the new bounds (~5–10 min)"
    : "Nudge an edge or pick a different speaker name";

  const heroTime = playheadUsable ? (
    <span style={{ fontFamily: "var(--ql-font-mono)", fontWeight: 700 }}>
      {" · "}
      {fmtClock(playheadClipSec)}
    </span>
  ) : null;

  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: 14,
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(255,255,255,.06)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {/* Zero-state strip: title + clip bar + the two hero buttons.
          Hidden entirely for upload jobs — no re-cuttable source. */}
      {trimmable && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 10,
              fontWeight: 700,
              color: "var(--ql-yellow)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
              whiteSpace: "nowrap",
            }}
          >
            <Scissors size={12} strokeWidth={2} />
            Cut &amp; extend
          </span>
          {/* Gated on hasDelta (not showDetail) so an engaged-but-reset trim
              can't contradict a speaker-only CTA's "card-only · ~1 min". */}
          {hasDelta && (
            <span
              style={{
                fontSize: 10,
                fontFamily: "var(--ql-font-mono)",
                color: "var(--ql-fg-subtle)",
                whiteSpace: "nowrap",
              }}
            >
              full re-run · ~5–10 min
            </span>
          )}
          <div style={{ flex: 1, minWidth: isMobile ? "100%" : 90 }}>
            <ClipBar
              lengthSec={contentLen ?? 30}
              startDelta={startDelta}
              endDelta={endDelta}
              playheadClipSec={contentLen !== null ? playheadClipSec : 0}
              onSeek={contentLen !== null ? seekToClipTime : undefined}
            />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={captureStart}
              disabled={!playheadUsable || busy}
              title={playheadUsable ? "Make the current playhead position the new start" : PLAYHEAD_TOOLTIP}
              style={heroBtnStyle(playheadUsable && !busy)}
            >
              <Crosshair size={12} strokeWidth={2} />
              Start here
              {heroTime}
            </button>
            <button
              type="button"
              onClick={captureEnd}
              disabled={!canEndHere || busy}
              title={canEndHere ? "Make the current playhead position the new end" : PLAYHEAD_TOOLTIP}
              style={heroBtnStyle(canEndHere && !busy)}
            >
              <Crosshair size={12} strokeWidth={2} />
              End here
              {heroTime}
            </button>
          </div>
        </div>
      )}

      {/* Smart Extend: one-line trigger on the RESTING card → design-system
          modal (transcript preview + AI suggestions + its own Apply).
          Absent unless the server reports the clip is actually extendable. */}
      {trimmable && <SmartExtendTrigger jobId={view.id} busy={busy} onJobStarted={onJobStarted} />}

      {/* Speaker quick-fix row — the most common card mistake is the HOST's
          name showing while the GUEST talks. Chips = the people this clip
          knows about; the fix rides the unified Apply below. */}
      <SpeakerRow
        candidates={candidates}
        name={speakerName}
        onChange={(v) => { setSpeakerName(v); setPostError(null); }}
        busy={busy}
      />

      {trimmable && baselineError && !showDetail && (
        <div style={{ fontSize: 11, color: "#FBBF24" }}>
          Trim preview unavailable: {baselineError}
        </div>
      )}

      {trimmable && showDetail && (
        <>
          <EdgeRow
            label="Start"
            chip={startChip(startDelta)}
            chipActive={Math.abs(startDelta) >= EPS}
            onNudge={nudgeStart}
            disabled={busy}
          />
          <EdgeRow
            label="End"
            chip={endChip(endDelta)}
            chipActive={Math.abs(endDelta) >= EPS}
            onNudge={nudgeEnd}
            disabled={busy}
          />

          {/* Preview line + warnings (debounced dry run). */}
          <div style={{ fontSize: 12, lineHeight: 1.55, minHeight: 18 }}>
            {previewPending ? (
              <span style={{ color: "var(--ql-fg-subtle)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Loader2 size={11} className="ql-spin" />
                checking the new bounds…
              </span>
            ) : previewError ? (
              <span style={{ color: "var(--ql-danger)", fontFamily: "var(--ql-font-mono)", fontSize: 11 }}>
                {previewError}
              </span>
            ) : preview && hasDelta ? (
              <span style={{ color: "var(--ql-fg)" }}>{previewLine(preview, startDelta, endDelta)}</span>
            ) : (
              <span style={{ color: "var(--ql-fg-subtle)" }}>
                Nudge an edge or capture the playhead to preview the new bounds.
              </span>
            )}
          </div>
          {!previewPending && preview && preview.warnings.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {preview.warnings.map((w) => (
                <div
                  key={w}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 6,
                    fontSize: 11,
                    color: "#FBBF24",
                    lineHeight: 1.45,
                  }}
                >
                  <AlertTriangle size={12} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {/* U8: audition the cut points — cuts only, hidden for extends. */}
          {(startDelta > 0.25 || endDelta < -0.25) && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {startDelta > 0.25 && (
                <button
                  type="button"
                  onClick={auditionStart}
                  title="Seek the preview player to the new start and play"
                  style={auditionBtnStyle}
                >
                  <Play size={11} strokeWidth={2} />
                  Preview new start
                </button>
              )}
              {endDelta < -0.25 && contentLen !== null && (
                <button
                  type="button"
                  onClick={auditionEnd}
                  title="Seek the preview player to ~3s before the new end and play"
                  style={auditionBtnStyle}
                >
                  <Play size={11} strokeWidth={2} />
                  Preview new end
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Unified CTA row — ONE Apply for bounds, speaker, or both. Appears as
          soon as anything changed (trim engaged OR a different speaker name). */}
      {(showDetail || speakerChanged) && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => void apply()}
              disabled={ctaDisabled}
              title={ctaTitle}
              style={{
                ...ctaSmallStyle,
                cursor: ctaDisabled ? "not-allowed" : "pointer",
                opacity: ctaDisabled ? 0.5 : 1,
              }}
            >
              {busy ? (
                <Loader2 size={14} className="ql-spin" />
              ) : mode === "speaker" ? (
                <UserRound size={14} strokeWidth={2} />
              ) : (
                <Scissors size={14} strokeWidth={2} />
              )}
              {ctaLabel}
            </button>
            <span style={{ fontSize: 11, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)" }}>
              {ctaCost}
            </span>
            <button
              type="button"
              onClick={reset}
              disabled={busy}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                color: "var(--ql-fg-muted)",
                fontSize: 12,
                fontFamily: "inherit",
                cursor: busy ? "not-allowed" : "pointer",
                textDecoration: "underline",
              }}
            >
              Reset
            </button>
          </div>

          {postError && (
            <div
              style={{
                background: "rgba(248,113,113,.06)",
                border: "1px solid rgba(248,113,113,.30)",
                borderRadius: 10,
                padding: "8px 12px",
                color: "var(--ql-danger)",
                fontSize: 12,
                fontFamily: "var(--ql-font-mono)",
              }}
            >
              {postError}
            </div>
          )}

          <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)" }}>
            The current render is archived — restore it anytime from Render history.
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Speaker chips + free input, one slim row inside the Adjust card. No button
 * of its own — the unified Apply below dispatches (card-only when nothing
 * else changed, riding the re-cut otherwise).
 */
function SpeakerRow({
  candidates,
  name,
  onChange,
  busy,
}: {
  candidates: string[];
  name: string;
  onChange: (v: string) => void;
  busy: boolean;
}): React.JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".08em",
          textTransform: "uppercase",
          color: "var(--ql-yellow)",
          whiteSpace: "nowrap",
        }}
      >
        <UserRound size={12} strokeWidth={2.2} /> Speaker
      </span>
      {candidates.map((c) => {
        const active = c.toLowerCase() === name.trim().toLowerCase();
        return (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            disabled={busy}
            title={active ? "Currently selected" : `Use "${c}"`}
            style={chipStyle(active, busy)}
          >
            {c}
          </button>
        );
      })}
      <input
        value={name}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Speaker name on the card"
        disabled={busy}
        style={{
          flex: 1,
          minWidth: 140,
          padding: "6px 10px",
          borderRadius: 8,
          fontSize: 12,
          fontFamily: "inherit",
          background: "rgba(255,255,255,.04)",
          border: "1px solid rgba(255,255,255,.12)",
          color: "var(--ql-fg)",
        }}
      />
    </div>
  );
}

/** "alex-hormozi" / "alex_hormozi" / "alex hormozi" → "Alex Hormozi"; anything
 *  already carrying an uppercase letter passes through untouched. The spaced-
 *  lowercase case matters: reclipJob rebuilds input.host as a humanized
 *  lowercase slug, and a chip showing that raw form would persist a lowercase
 *  display name into the operator-speaker override when tapped. */
function prettifySlug(s?: string): string | undefined {
  const v = (s ?? "").trim();
  if (!v) return undefined;
  if (/[A-Z]/.test(v)) return v; // already a display name
  return v
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function chipStyle(active: boolean, busy: boolean): CSSProperties {
  return {
    padding: "5px 11px",
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 600,
    fontFamily: "inherit",
    background: active ? "rgba(255,213,0,.14)" : "rgba(255,255,255,.04)",
    border: active ? "1px solid rgba(255,213,0,.45)" : "1px solid rgba(255,255,255,.14)",
    color: active ? "var(--ql-yellow)" : "var(--ql-fg)",
    cursor: busy ? "wait" : "pointer",
  };
}

/**
 * One edge's fine-nudge controls: two direction-coded stepper pairs with
 * persistent "earlier"/"later" micro-labels (U1/U6), plus the value chip.
 * Blue = earlier on the timeline, yellow = later — direction, not add/remove,
 * matching the caption-sync buttons.
 */
function EdgeRow({
  label,
  chip,
  chipActive,
  onNudge,
  disabled,
}: {
  label: string;
  chip: string;
  chipActive: boolean;
  onNudge: (d: number) => void;
  disabled: boolean;
}): React.JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
      <span
        style={{
          width: 38,
          paddingTop: 7,
          fontSize: 10,
          fontWeight: 700,
          color: "var(--ql-fg-muted)",
          textTransform: "uppercase",
          letterSpacing: ".08em",
        }}
      >
        {label}
      </span>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
        <div style={{ display: "flex", gap: 4 }}>
          <button type="button" onClick={() => onNudge(-5)} disabled={disabled} style={stepBtnStyle("earlier", disabled)}>
            «5s
          </button>
          <button type="button" onClick={() => onNudge(-1)} disabled={disabled} style={stepBtnStyle("earlier", disabled)}>
            «1s
          </button>
        </div>
        <span style={microLabelStyle}>earlier</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
        <div style={{ display: "flex", gap: 4 }}>
          <button type="button" onClick={() => onNudge(1)} disabled={disabled} style={stepBtnStyle("later", disabled)}>
            1s»
          </button>
          <button type="button" onClick={() => onNudge(5)} disabled={disabled} style={stepBtnStyle("later", disabled)}>
            5s»
          </button>
        </div>
        <span style={microLabelStyle}>later</span>
      </div>
      <span
        style={{
          marginLeft: "auto",
          paddingTop: 7,
          fontSize: 12,
          fontFamily: "var(--ql-font-mono)",
          fontWeight: 700,
          color: chipActive ? "var(--ql-yellow)" : "var(--ql-fg-subtle)",
          whiteSpace: "nowrap",
        }}
      >
        {chip}
      </span>
    </div>
  );
}

/**
 * Slim clip-timeline bar (U3). The solid body is the CURRENT mp4 content;
 * cut regions dim/hatch at its edges, extension segments render as dashed
 * outlines beyond the edges. Clicking the body seeks the shared player to
 * that position; clicking an extension seeks to the nearest real edge
 * (its content doesn't exist in the current mp4 yet).
 */
function ClipBar({
  lengthSec,
  startDelta,
  endDelta,
  playheadClipSec,
  onSeek,
}: {
  lengthSec: number;
  startDelta: number;
  endDelta: number;
  playheadClipSec: number;
  /** Absent while the real clip length is unknown — the bar renders inert. */
  onSeek?: (clipT: number) => void;
}): React.JSX.Element {
  const L = Math.max(1, lengthSec);
  const cutL = clamp(Math.max(0, startDelta), 0, L);
  const extL = Math.max(0, -startDelta);
  const cutR = clamp(Math.max(0, -endDelta), 0, L - cutL);
  const extR = Math.max(0, endDelta);

  const onBodyClick = (e: ReactMouseEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    const frac = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    onSeek?.(frac * L);
  };

  const hatch =
    "repeating-linear-gradient(135deg, rgba(255,255,255,.16) 0px, rgba(255,255,255,.16) 2px, transparent 2px, transparent 5px)";

  const extStyle = (side: "left" | "right"): CSSProperties => ({
    flexGrow: side === "left" ? extL : extR,
    flexBasis: 0,
    minWidth: 10,
    border: "1.5px dashed rgba(255,213,0,.55)",
    borderRadius: 6,
    background: "rgba(255,213,0,.06)",
    cursor: "pointer",
  });

  const pctOf = (n: number): string => `${((n / L) * 100).toFixed(2)}%`;

  return (
    <div style={{ display: "flex", alignItems: "stretch", gap: 3, height: 12 }}>
      {extL > 0 && (
        <div
          onClick={() => onSeek?.(0)}
          title={`+${extL.toFixed(1)}s added before the current start`}
          style={extStyle("left")}
        />
      )}
      <div
        onClick={onBodyClick}
        title="Click to seek the preview player"
        style={{
          position: "relative",
          flexGrow: L,
          flexBasis: 0,
          borderRadius: 6,
          overflow: "hidden",
          background: "rgba(255,255,255,.05)",
          cursor: "pointer",
        }}
      >
        {/* Kept region. */}
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: pctOf(cutL),
            right: pctOf(cutR),
            background: "linear-gradient(90deg, rgba(255,213,0,.65), rgba(255,165,0,.65))",
          }}
        />
        {/* Cut regions — dimmed + hatched. */}
        {cutL > 0 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: pctOf(cutL),
              background: hatch,
            }}
          />
        )}
        {cutR > 0 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              right: 0,
              width: pctOf(cutR),
              background: hatch,
            }}
          />
        )}
        {/* Playhead marker. */}
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: pctOf(clamp(playheadClipSec, 0, L)),
            width: 2,
            background: "rgba(255,255,255,.85)",
            transform: "translateX(-1px)",
            pointerEvents: "none",
          }}
        />
      </div>
      {extR > 0 && (
        <div
          onClick={() => onSeek?.(L)}
          title={`up to +${extR.toFixed(1)}s added after the current end`}
          style={extStyle("right")}
        />
      )}
    </div>
  );
}
